/**
 * Planetary Positions — geocentric sidereal (Lahiri) positions of the nine
 * grahas in a tile grid.
 *
 * Visual grammar (docs/STYLE_GUIDE.md): unified parchment cards on the warm
 * token surface; each graha keeps its Vedic-association color, but only as a
 * 4px identity band + icon chip — never a full card wash — so the grid reads as
 * one product. Per the style guide, green/red stay reserved for favourable /
 * avoid, so motion is NOT colored that way: DIRECT is quiet muted text, and
 * RETROGRADE is the one emphasized state (brand-maroon ℞ chip, --kg-retro).
 */

import React from 'react';
import { Globe } from 'lucide-react';
import { usePlanetaryPositions } from '../../hooks/usePlanetaryPositions';
import type { DMS, PlanetPosition } from '../../types/api';
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner';
import ErrorDisplay from '../ErrorDisplay/ErrorDisplay';
import { GRAHA_WARM_COLORS, GRAHA_WARM_DEFAULT } from '../../lib/grahaVisual';

interface PlanetaryPositionsProps {
  date?: string;
  time?: string;
}

// Vedic association: one identity color + glyph per graha. Used only for the
// 4px band + chip (exempt graha-color map, like elsewhere in the app).
// Glyphs are local; identity colors come from the shared warm palette
// (lib/grahaVisual.ts) so this page and the Daily Pañcāṅga glance list stay in
// lockstep — a Sūrya dot is the same gold on both parchment surfaces.
const GRAHA_VISUAL: Record<string, { glyph: string; color: string }> = {
  Sun:     { glyph: '☉', color: GRAHA_WARM_COLORS.Sun },
  Moon:    { glyph: '☾', color: GRAHA_WARM_COLORS.Moon },
  Mars:    { glyph: '♂', color: GRAHA_WARM_COLORS.Mars },
  Mercury: { glyph: '☿', color: GRAHA_WARM_COLORS.Mercury },
  Jupiter: { glyph: '♃', color: GRAHA_WARM_COLORS.Jupiter },
  Venus:   { glyph: '♀', color: GRAHA_WARM_COLORS.Venus },
  Saturn:  { glyph: '♄', color: GRAHA_WARM_COLORS.Saturn },
  Rahu:    { glyph: '☊', color: GRAHA_WARM_COLORS.Rahu },
  Ketu:    { glyph: '☋', color: GRAHA_WARM_COLORS.Ketu },
};
const DEFAULT_VISUAL = { glyph: '•', color: GRAHA_WARM_DEFAULT };

const formatDMS = (dms: DMS): string => `${dms.degrees}° ${dms.minutes}' ${dms.seconds}"`;

// Rāhu/Ketu are math points — the backend reports a placeholder distance we
// render as an em-free dash rather than a misleading "0.0000 AU".
const formatDistance = (d: number): string =>
  Number.isFinite(d) && d > 0 ? `${d.toFixed(4)} AU` : '-';

/**
 * Shared page shell so every state (loading/error/empty/data) keeps one header.
 * `kg-warm` here only scopes the warm tokens — it paints no background; the
 * header sits directly on the page and the graha cards are the only panels
 * (mirrors DailyPanchangaView).
 */
const Frame: React.FC<{ subtitle: React.ReactNode; children: React.ReactNode }> = ({ subtitle, children }) => (
  <div className="kg-warm space-y-5" style={{ color: 'var(--kg-ink)' }}>
    <header className="flex flex-col gap-3 px-1 sm:flex-row sm:items-start sm:justify-between">
      <div className="flex items-start gap-3">
        <Globe className="mt-1 h-5 w-5 shrink-0" style={{ color: 'var(--kg-accent)' }} aria-hidden="true" />
        <div>
          <div className="flex flex-wrap items-center gap-2.5">
            <h2 className="font-fraunces text-[22px] font-semibold leading-none" style={{ color: 'var(--kg-accent)' }}>
              Graha Sthāna
              <span className="font-normal" style={{ color: 'var(--kg-sub)' }}> · Planetary Positions</span>
            </h2>
            {/* Ephemeris credibility badge — same language as the Landing hero badge. */}
            <span
              className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10.5px] font-semibold uppercase tracking-[0.04em]"
              style={{ background: 'var(--kg-badge-bg)', color: 'var(--kg-faint)', border: '1px solid var(--kg-badge-border)' }}
            >
              <span
                className="inline-block h-1.5 w-1.5 rounded-full"
                style={{ background: 'var(--kg-green)', boxShadow: '0 0 0 3px rgba(30,158,106,0.18)' }}
              />
              Swiss Ephemeris · Lahiri Ayanāṃśa
            </span>
          </div>
          <p className="mt-1 text-[12.5px]" style={{ color: 'var(--kg-sub)' }}>{subtitle}</p>
        </div>
      </div>
      {/* Legend — motion follows Option 1: quiet Direct, maroon ℞ Retrograde. */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[11px]" style={{ color: 'var(--kg-sub)' }}>
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block h-1.5 w-1.5 rounded-full" style={{ background: 'var(--kg-faint)' }} />
          Direct
        </span>
        <span className="inline-flex items-center gap-1.5 font-semibold" style={{ color: 'var(--kg-retro)' }}>
          ℞ Retrograde
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-flex" aria-hidden="true">
            <span className="inline-block h-1.5 w-1.5 rounded-full" style={{ background: GRAHA_VISUAL.Sun.color }} />
            <span className="-ml-0.5 inline-block h-1.5 w-1.5 rounded-full" style={{ background: GRAHA_VISUAL.Mercury.color }} />
            <span className="-ml-0.5 inline-block h-1.5 w-1.5 rounded-full" style={{ background: GRAHA_VISUAL.Saturn.color }} />
          </span>
          Band = graha (Vedic association)
        </span>
      </div>
    </header>
    {children}
  </div>
);

// Caps section label stacked over its value (RĀŚI / NAKṢATRA blocks).
const Stat: React.FC<{ label: string; children: React.ReactNode; align?: 'left' | 'right' }> = ({ label, children, align = 'left' }) => (
  <div className={align === 'right' ? 'text-right' : ''}>
    <div className="text-[10px] font-medium uppercase tracking-wide" style={{ color: 'var(--kg-faint)' }}>{label}</div>
    <div className="mt-0.5 font-fraunces text-[15px] font-medium" style={{ color: 'var(--kg-ink)' }}>{children}</div>
  </div>
);

// Inline "Label ....... 12° 34' 56"  12.34°" row for longitude / in-sign.
const DegreeRow: React.FC<{ label: string; dms: DMS; decimal: number }> = ({ label, dms, decimal }) => (
  <div className="flex items-baseline justify-between gap-3">
    <span className="text-[12.5px]" style={{ color: 'var(--kg-sub)' }}>{label}</span>
    <span className="text-[12.5px] tabular-nums" style={{ color: 'var(--kg-ink)' }}>
      {formatDMS(dms)}{' '}
      <span style={{ color: 'var(--kg-faint)' }}>{decimal.toFixed(4)}°</span>
    </span>
  </div>
);

const hairline = { borderTop: '1px solid var(--kg-line)' } as const;

const PlanetCard: React.FC<{ planet: PlanetPosition }> = ({ planet }) => {
  const visual = GRAHA_VISUAL[planet.planet_english] || DEFAULT_VISUAL;
  const isRetro = planet.motion === 'RETROGRADE';

  return (
    <article
      className="kg-warm overflow-hidden rounded-2xl transition-shadow hover:shadow-md"
      style={{ background: 'var(--kg-card)', border: '1px solid var(--kg-line)', boxShadow: 'var(--kg-shadow)' }}
    >
      {/* 4px Vedic-association identity band */}
      <div style={{ height: 4, background: visual.color }} aria-hidden="true" />
      <div className="p-5">
        {/* Header: chip + name + Sanskrit */}
        <div className="mb-4 flex items-center gap-3">
          <span
            className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[15px]"
            style={{ color: visual.color, border: `1.5px solid ${visual.color}`, background: `${visual.color}1a` }}
            aria-hidden="true"
          >
            {visual.glyph}
          </span>
          <div className="min-w-0">
            <h3 className="font-fraunces text-[18px] font-semibold leading-tight" style={{ color: 'var(--kg-ink)' }}>
              {planet.planet_english}
            </h3>
            <p className="text-[12.5px]" style={{ color: 'var(--kg-sub)' }}>{planet.planet_sanskrit}</p>
          </div>
        </div>

        {/* Rāśi + Rāśi No. */}
        <div className="flex items-start justify-between gap-3 pt-3" style={hairline}>
          <Stat label="Rāśi (zodiac sign)">
            {planet.zodiac_sign} <span style={{ color: 'var(--kg-sub)' }}>({planet.zodiac_sign_sanskrit})</span>
          </Stat>
          <Stat label="Rāśi no." align="right">{planet.zodiac_sign_number}</Stat>
        </div>

        {/* Nakṣatra + Pāda (backend-computed, same canon as the birth chart) */}
        <div className="mt-3 flex items-start justify-between gap-3 pt-3" style={hairline}>
          <Stat label="Nakṣatra">{planet.nakshatra_name}</Stat>
          <Stat label="Pāda" align="right">{planet.pada}</Stat>
        </div>

        {/* Longitude + In sign */}
        <div className="mt-3 space-y-1.5 pt-3" style={hairline}>
          <DegreeRow label="Longitude" dms={planet.longitude_dms} decimal={planet.longitude_degrees} />
          <DegreeRow label="In sign" dms={planet.degrees_in_sign_dms} decimal={planet.degrees_in_sign} />
        </div>

        {/* Motion */}
        <div className="mt-3 flex items-center justify-between gap-3 pt-3" style={hairline}>
          <span className="text-[10px] font-medium uppercase tracking-wide" style={{ color: 'var(--kg-faint)' }}>Motion</span>
          {isRetro ? (
            <span
              className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide"
              style={{ color: 'var(--kg-retro)', border: '1px solid var(--kg-retro)' }}
            >
              ℞ Retrograde
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide" style={{ color: 'var(--kg-sub)' }}>
              <span className="inline-block h-1.5 w-1.5 rounded-full" style={{ background: 'var(--kg-faint)' }} />
              Direct
            </span>
          )}
        </div>

        {/* Latitude · Distance · Speed */}
        <div className="mt-3 grid grid-cols-3 gap-2 pt-3" style={hairline}>
          {[
            { label: 'Latitude', value: `${planet.latitude_degrees.toFixed(4)}°` },
            { label: 'Distance', value: formatDistance(planet.distance) },
            { label: 'Speed', value: `${planet.speed.toFixed(4)}°/day` },
          ].map(cell => (
            <div key={cell.label}>
              <div className="text-[9.5px] font-medium uppercase tracking-wide" style={{ color: 'var(--kg-faint)' }}>{cell.label}</div>
              <div className="mt-0.5 text-[12px] tabular-nums" style={{ color: 'var(--kg-sub2)' }}>{cell.value}</div>
            </div>
          ))}
        </div>
      </div>
    </article>
  );
};

export const PlanetaryPositions: React.FC<PlanetaryPositionsProps> = ({ date, time }) => {
  const params = date || time ? { date, time } : undefined;
  const { data, loading, error, refetch } = usePlanetaryPositions(params);

  const subtitle =
    data?.date && data?.time
      ? `Geocentric sidereal positions as of ${data.date} ${data.time}`
      : 'Current geocentric sidereal positions';

  if (loading) {
    return (
      <Frame subtitle={subtitle}>
        <LoadingSpinner message="Loading planetary positions..." />
      </Frame>
    );
  }

  if (error) {
    return (
      <Frame subtitle={subtitle}>
        <ErrorDisplay message={error} onRetry={refetch} />
      </Frame>
    );
  }

  if (!data || !data.planets || data.planets.length === 0) {
    return (
      <Frame subtitle={subtitle}>
        <p className="text-[13px]" style={{ color: 'var(--kg-sub)' }}>No planetary positions data available.</p>
      </Frame>
    );
  }

  return (
    <Frame subtitle={subtitle}>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
        {data.planets.map((planet, index) => (
          <PlanetCard key={planet.planet_english || index} planet={planet} />
        ))}
      </div>
    </Frame>
  );
};

export default PlanetaryPositions;
